<template>
  <div class="send-exams-container">
    <InitialView
      v-if="status === 'initial'"
      @main-action="chooseFile()"
      @send-files="sendFiles()"
      @secondary-action="openContinueWithoutExams()"
      @remove="removeFile"
      :files="fileList"
    />
    <LoadingView v-if="status === 'uploading'" :showLabel="hasFileToSendToOCR" />
    <ExamsIdentifiedSuccess v-else-if="status === 'done'" />
    <ExamsIdentifiedWarning
      v-else-if="status === 'warning'"
      @secondary-action="openContinueWithoutExams"
    />
    <ExamsNotIdentified
      v-else-if="status === 'not_identified'"
      @secondary-action="openContinueWithoutExams"
      @review-orders="returnInitialView"
    />
    <FileUploadSuccess v-else-if="status === 'success_without_ocr'" />

    <input
      type="file"
      name="file-chooser"
      id="file-chooser"
      ref="input"
      multiple
      @change="handleFileChange"
    />
  </div>
</template>

<script>
import {
  InitialView,
  LoadingView,
  ExamsIdentifiedSuccess,
  ExamsIdentifiedWarning,
  ExamsNotIdentified,
  FileUploadSuccess,
} from './partials';
import { mapActions, mapState, mapGetters, mapMutations } from 'vuex';
import { dataLayersMixin } from '@/utils/mixins';
import { removeQueryParamsFromUrl } from '@/utils/helpers/removeQueryParamsFromUrl';

const DEFAULT_DELAY = 4000;

export default {
  mixins: [dataLayersMixin],
  components: {
    InitialView,
    LoadingView,
    ExamsIdentifiedSuccess,
    ExamsIdentifiedWarning,
    ExamsNotIdentified,
    FileUploadSuccess,
  },
  created() {
    this.checkAdmissionIdsOnMedicalOrders();
  },

  mounted() {
    this.fileList = this.getUploadedMedicalOrders;
  },

  data() {
    return {
      status: 'initial',
      fileList: [],
      examsFromOcr: [],
      showContinueWithoutAdvice: false,
      idPaperless: '',
      limitFiles: 10,
      allowedExtensions: ['png', 'jpeg', 'jpg', 'pdf'],
      isRetryingAfterOcrError: false,
    };
  },

  computed: {
    ...mapState({
      currentAdmission: ({ currentAdmission }) => currentAdmission.data,
      admissionId: ({ currentAdmission }) => currentAdmission.data.id,
      payment: ({ payment }) => payment.data,
      healthInsuranceVariables: ({ healthInsurance }) =>
        healthInsurance.data.healthInsuranceVariables,
    }),
    ...mapGetters({
      getInsurancePlan: 'getInsurancePlan',
      isOcrEnableInUnit: 'isOcrEnableInUnit',
      getUploadedMedicalOrders: 'getUploadedMedicalOrders',
      hasMedicalOrdersSentToAnalyze: 'hasMedicalOrdersSentToAnalyze',
      getBrandUnitIdVariables: 'getBrandUnitIdVariables',
      getMedicalOrders: 'getMedicalOrders',
    }),
    hasFileToSendToOCR() {
      return !!this.getUploadedMedicalOrders.filter((item) => item.data)?.length;
    },
  },

  methods: {
    ...mapActions([
      'uploadFilesOnPaperless',
      'sendFilesToOcr',
      'getQuestionnaire',
      'checkAdmissionIdsOnMedicalOrders',
      'createMedicalOrderByOcr',
      'updateMedicalOrderUrl',
    ]),
    ...mapMutations([
      'SET_UPLOADED_MEDICAL_ORDERS',
      'SET_CLEAR_MEDICALORDERS',
      'SET_SEND_TO_ANALYZE',
    ]),
    chooseFile() {
      this.$refs.input.click();
    },

    async sendFiles() {
      this.status = 'uploading';

      const beforeUnloadListener = (event) => {
        event.preventDefault();
        return (event.returnValue = 'Tem certeza de que deseja sair da página?');
      };
      addEventListener('beforeunload', beforeUnloadListener, { capture: true });

      try {
        this.removeInvalidFiles();

        try {
          const ocrOk = await this.handleOcr();
          if (!ocrOk) return;

          this.SET_SEND_TO_ANALYZE(true);
          this.mostCommonPagesAnalytics({
            eventType: 'funnel_step',
            currentStep: 'Pedido médico enviado com sucesso',
          });
        } catch (error) {
          console.error('[handleOcr ERROR]', error);
        }

        const created = await this.handleCreateMedicalOrder();

        if (['done', 'success_without_ocr'].includes(this.status)) {
          const updated = await this.handleUpdateMedicalOrderUrl();
          if (!updated) return;
        }

        if (!created) return;

        const promise1 = new Promise((resolve) => setTimeout(() => resolve(true), DEFAULT_DELAY));
        const promise2 = this.getQuestionnaire();

        await Promise.all([promise1, promise2]);
        this.status = 'uploaded';
        this.goNextStep('questions_step', { answer: 'Enviar pedido médico' });
      } finally {
        removeEventListener('beforeunload', beforeUnloadListener, { capture: true });
      }
    },

    async handleOcr() {
      if (
        this.isOcrEnableInUnit &&
        this.hasFileToSendToOCR &&
        !this.hasMedicalOrdersSentToAnalyze
      ) {
        const formattedFiles = this.getUploadedMedicalOrders.map((item) => ({
          paperlessId: item.data.paperlessId,
          urlFile: removeQueryParamsFromUrl(item.data?.documentUrl),
        }));

        try {
          const ocrResponse = await this.sendFilesToOcr({
            admissionId: this.admissionId,
            unitId: this.getBrandUnitIdVariables.unitId,
            files: formattedFiles,
          });

          this.examsFromOcr = ocrResponse
            .filter((item) => Array.isArray(item.exams) && item.exams.length > 0)
            .flatMap((item) => item.exams);

          const hasInvalidFile = ocrResponse.some((item) =>
            ['FILE_EMPTY', 'IS_NOT_MEDICAL_ORDER'].includes(item.errorCode),
          );

          if (hasInvalidFile) {
            this.status = this.isRetryingAfterOcrError ? 'success_without_ocr' : 'not_identified';

            if (this.status === 'success_without_ocr') {
              await new Promise((resolve) => setTimeout(resolve, DEFAULT_DELAY));
              await this.getQuestionnaire();
              this.goNextStep('questions_step', { answer: 'Enviar pedido médico' });
              this.status = 'uploaded';
              return true;
            }

            return false;
          }
        } catch (error) {
          this.examsFromOcr = [];
          this.ocrErrorCode = null;
        }
      }

      return true;
    },

    async handleCreateMedicalOrder() {
      const firstPayment = this.currentAdmission?.payments?.[0];
      if (!firstPayment?.clientId) {
        this.status = 'warning';
        return false;
      }

      const exams = (this.examsFromOcr || []).map((item) => ({
        examCode: item.exam_code,
      }));

      const negotiationVars = firstPayment?.details?.negotiationVariables || {};

      const hasMedicalOrder =
        Array.isArray(this.getMedicalOrders) && this.getMedicalOrders.length > 0;

      const hasMedicalOrderWithExams =
        hasMedicalOrder &&
        this.getMedicalOrders.some((order) => Array.isArray(order.exams) && order.exams.length > 0);

      const isAsyncRoutine = this.ocrErrorCode === 'ASYNC_ROUTINE';

      if (hasMedicalOrderWithExams || isAsyncRoutine) {
        this.status = 'done';
        return true;
      }

      if (!this.examsFromOcr || this.examsFromOcr.length === 0) {
        this.status = 'done';
        return true;
      }

      try {
        await this.createMedicalOrderByOcr({
          exams,
          clientId: firstPayment.clientId,
          unitId: this.getBrandUnitIdVariables.unitId,
          negotiationVariables: negotiationVars,
        });

        this.status = 'done';
        return true;
      } catch (err) {
        this.status = 'warning';
        return false;
      }
    },

    async handleUpdateMedicalOrderUrl() {
      try {
        const paperlessId = this.getUploadedMedicalOrders
          ?.map((item) => item.data?.paperlessId)
          .filter(Boolean);

        if (!paperlessId.length) {
          this.status = 'warning';
          return false;
        }

        await this.updateMedicalOrderUrl({
          admissionId: this.admissionId,
          idPaperless: paperlessId,
        });
        return true;
      } catch (error) {
        this.status = 'warning';
        return false;
      }
    },

    async openContinueWithoutExams() {
      await this.$router.push({ name: 'questions', params: { answer: 'Não tenho pedido médico' } });
    },

    async returnInitialView() {
      this.status = 'initial';
      this.isRetryingAfterOcrError = true;
    },

    removeFile(index) {
      this.fileList.splice(index, 1);
      this.SET_UPLOADED_MEDICAL_ORDERS(this.fileList);
    },

    removeInvalidFiles() {
      this.fileList = this.fileList.filter((item) => item.data.status !== 'ERROR');
      this.SET_UPLOADED_MEDICAL_ORDERS(this.fileList);
    },

    async handleFileChange({ target: { files = [] } }) {
      try {
        if (this.fileList.length < this.limitFiles) {
          for await (const file of files) {
            const fileExtension = file.name.split('.').pop().toLowerCase();
            const allowedFile = this.allowedExtensions.includes(fileExtension);

            if (!allowedFile) {
              const fileObject = {
                file,
                status: 'UPLOADED',
                data: {
                  status: 'ERROR',
                  name: file.name,
                },
              };
              this.fileList = [...this.fileList, fileObject];
              this.SET_UPLOADED_MEDICAL_ORDERS(this.fileList);
              continue;
            }

            const fileObject = {
              file,
              status: 'UPLOADING',
              data: {
                status: 'UPLOADING',
                name: file.name,
              },
            };

            this.fileList = [...this.fileList, fileObject];
            this.SET_UPLOADED_MEDICAL_ORDERS(this.fileList);

            let uploadFile;
            try {
              uploadFile = await this.uploadFilesOnPaperless({ file });
            } catch (err) {
              console.error('Error to upload file to paperless! > ', err);
            }

            const findFile = this.fileList.find((item) => item === fileObject);

            if (uploadFile) {
              findFile.data = {
                ...findFile.data,
                ...uploadFile,
                status: 'UPLOADED',
                admissionId: this.admissionId,
              };
            }
          }

          this.SET_UPLOADED_MEDICAL_ORDERS(this.fileList);
          this.mostCommonPagesAnalytics({
            eventType: 'funnel_step',
            currentStep: 'Pedido médico adicionado',
          });
        }
      } catch (error) {
        this.mostCommonPagesAnalytics({
          eventType: 'funnel_step',
          currentStep: 'Erro ao adicionar Pedido médico',
        });
      } finally {
        this.$refs.input.value = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.send-exams-container {
  flex: 1;
  padding: 0;
  display: flex;
  flex-direction: column;
  height: auto;
  justify-content: space-between;
  align-items: center;
  align-content: center;
  position: relative;
  overflow: hidden;

  input {
    display: none;
  }
}

.advice-before-leave {
  flex: 1;
  justify-content: space-between;
  display: flex;
  flex-direction: column;
  font-family: 'Dasa sans', sans-serif;

  &__info {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  h4 {
    font-size: 16px;
    line-height: 28px;
    color: #323232;
    font-weight: var(--font-weight-bold);
    margin-bottom: 8px;
  }

  p {
    font-size: 16px;
    line-height: 20.8px;
    color: #161616b8;
    padding: 0 17px;
    margin-bottom: 24px;
  }

  button {
    padding: 12px 0;
    text-align: center;
    background: #652cdf;
    border: 1px solid #652cdf;
    border-radius: 4px;
    color: white;
    font-size: 16px;
    line-height: 24px;
    font-weight: var(--font-weight-bold);
  }
}
</style>
